import { type FC, PropsWithChildren, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import Icon from '__components/Kizen/Icon';
import { IconSizing } from '__components/Kizen/Icon';
import PercentageBar from 'components/PercentageBar';
import { KizenTypography } from '__app/typography';
import { isNotSupportedFile } from './helpers';
import { useFlashTransition } from 'hooks/useFlashState';
import {
  ChooserDragAndDrop,
  CloudIcon,
  ErrorFileTypography,
  ErrorSpacedTypography,
  LoadingBar,
  SpacedTypography,
  StyledPlaceholder,
  StyleErrorCard,
} from './styles';
import { MAX_FILE_SIZE_BYTES } from '@kizen/api/file';
import { applyRef } from 'components/Inputs/props';

const FilePlaceholder: FC<{ file: File }> = (props) => {
  const { file } = props;
  return (
    <StyledPlaceholder {...props}>
      <IconSizing size="35px" style={{ marginTop: '7px' }}>
        <Icon icon="file" />
      </IconSizing>
      <KizenTypography as="span">{file.name}</KizenTypography>
    </StyledPlaceholder>
  );
};

const DashedBorder: FC<
  PropsWithChildren<{ color: string; dashLength?: number; hidden?: boolean }>
> = ({ children, color, dashLength = 4, hidden }) => (
  <>
    {children}

    {!hidden && (
      <svg
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-0 left-0 pointer-events-none"
      >
        <rect
          width="100%"
          height="100%"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={dashLength}
          strokeDashoffset="1"
          strokeLinecap="butt"
        />
      </svg>
    )}
  </>
);

const FilePicker: FC<{
  disabled?: boolean;
  onFileAdded: (files: File[]) => void;
  allowedFileExtensions: string[];
  uploadStatus?: number;
  multiFileMode?: boolean;
  isMobile?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}> = ({
  disabled,
  onFileAdded, // [File,...]
  allowedFileExtensions,
  uploadStatus = 0, // 0 >> 100
  multiFileMode = false,
  isMobile = false,
  inputRef,
}) => {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState<File[] | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [validateMessage, showValidateMessage, setValidateMessage] =
    useFlashTransition();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const hasExceedingLimit = acceptedFiles.find(
        (e) => e.size > MAX_FILE_SIZE_BYTES
      );

      if (hasExceedingLimit) {
        setValidateMessage(
          t(
            'Your file exceeds the maximum size of 500MB.  Please try again with a smaller file.'
          )
        );
        return;
      }

      const badFiles = acceptedFiles.filter(
        isNotSupportedFile(allowedFileExtensions)
      );
      if (badFiles.length === 0) {
        const limitedFiles = multiFileMode
          ? acceptedFiles
          : [acceptedFiles.pop() as File];
        setFileError(null);
        setUploadingFiles(limitedFiles);
        onFileAdded(limitedFiles);
      } else {
        setFileError(badFiles);
      }
      setFocused(false);
    },
    [allowedFileExtensions, multiFileMode, onFileAdded, setValidateMessage, t]
  );
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDragEnter: () => {
      setFocused(true);
    },
    onDragLeave: () => {
      setFocused(false);
    },
  });

  // @ts-expect-error DropzoneInputProps type def from 'react-dropzone' is missing ref.
  const { ref, ...inputProps } = getInputProps();
  const mergeRef = (el: HTMLInputElement) => {
    applyRef(ref, el);
    applyRef(inputRef, el);
  };

  const hasSolidBorder = !!uploadStatus && multiFileMode;
  const isError = !!fileError || showValidateMessage;
  const dashColor = focused
    ? 'var(--color-button-secondary-hover)'
    : isError
      ? 'var(--color-button-warning-hover)'
      : disabled
        ? 'var(--color-border-medium-light)'
        : hovered
          ? 'var(--color-button-secondary-hover)'
          : 'var(--color-button-disabled-default)';

  return (
    <ChooserDragAndDrop
      {...{ ...(!disabled && getRootProps()) }}
      status={!!uploadStatus}
      disabled={disabled}
      multiFileMode={multiFileMode}
      isMobile={isMobile}
      focused={focused}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <DashedBorder color={dashColor} hidden={hasSolidBorder}>
        {!fileError ? (
          <>
            {!uploadStatus ? (
              !multiFileMode && uploadingFiles?.length ? (
                <FilePlaceholder
                  file={uploadingFiles[uploadingFiles.length - 1]}
                />
              ) : (
                <CloudIcon size="50px">
                  <Icon
                    icon="cloud-arrow-up"
                    color={
                      disabled
                        ? 'var(--color-background-medium)'
                        : 'var(--color-background-dark)'
                    }
                  />
                </CloudIcon>
              )
            ) : (
              <LoadingBar multiFileMode={multiFileMode}>
                <PercentageBar
                  percentage={uploadStatus}
                  width={isMobile ? 100 : 220}
                  radius={14}
                  primaryColor="var(--color-button-secondary-hover)"
                  backgroundColor="var(--color-background-medium)"
                  responsive={false}
                />
                <KizenTypography weight="bold">{uploadStatus}%</KizenTypography>
              </LoadingBar>
            )}
            <SpacedTypography
              singleMode={!multiFileMode && !!uploadingFiles?.length}
            >
              <KizenTypography
                as="span"
                weight="bold"
                color={
                  disabled
                    ? 'var(--color-viz-cat-2-secondary)'
                    : 'var(--color-button-secondary-default)'
                }
              >
                {multiFileMode
                  ? t('Choose Files')
                  : uploadingFiles?.length
                    ? t('Change File')
                    : t('Choose File')}
                {` `}
              </KizenTypography>
              {isMobile ? null : (
                <KizenTypography
                  as="span"
                  color={
                    disabled
                      ? 'var(--color-background-medium)'
                      : 'var(--color-font-placeholder)'
                  }
                >
                  {multiFileMode
                    ? t('or drag & drop files here')
                    : t('or drag & drop here')}
                </KizenTypography>
              )}
            </SpacedTypography>
          </>
        ) : (
          <>
            <IconSizing size="35px">
              <Icon icon={'file-alt'} color="var(--color-background-dark)" />
            </IconSizing>
            <ErrorFileTypography
              color="var(--color-button-warning-default)"
              weight="bold"
            >
              {fileError[0].name}
            </ErrorFileTypography>
            <ErrorSpacedTypography>
              <KizenTypography
                as="span"
                weight="bold"
                color="var(--color-button-secondary-default)"
              >
                {t('Change File')}
                {` `}
              </KizenTypography>
              {isMobile ? null : (
                <KizenTypography
                  as="span"
                  color="var(--color-font-placeholder)"
                >
                  {t('or drag & drop files here')}
                </KizenTypography>
              )}
            </ErrorSpacedTypography>
          </>
        )}
        <input
          ref={mergeRef}
          disabled={disabled}
          type="file"
          hidden
          {...inputProps}
          multiple={multiFileMode}
          accept={allowedFileExtensions.join(',')}
        />
        {validateMessage && (
          <StyleErrorCard show={showValidateMessage} duration="300ms">
            <KizenTypography>{validateMessage}</KizenTypography>
          </StyleErrorCard>
        )}
      </DashedBorder>
    </ChooserDragAndDrop>
  );
};

export default FilePicker;
